import { createReducer, on } from '@ngrx/store';
import { BookingState } from '@products/models/booking.model';
import { TariffUuids } from '@products/models/tariff.model';
import { Hash } from '@store/helpers/helper.interface';
import { BookingActions } from '@store/products/booking/booking.actions';
import { BookingCountdownActions } from '@store/products/booking/countdown/booking-countdown.actions';
import {
  removeProductBookingCountdowns,
  removeVoucherProductBookingCountdown,
  setProductBookingCountdown,
  setVoucherProductBookingCountdown
} from '@store/products/booking/countdown/booking-countdown.reducer';
import { AmanoParkingBookingReservationActions } from '@store/products/booking/product/amano-parking/amano-parking-booking.actions';
import {
  setAmanoParkingReservationDateInBooking,
  setValidatedAmanoParkingReservationDateInBooking
} from '@store/products/booking/product/amano-parking/amano-parking-booking.reducer';
import { ContingentBookingReservationActions } from '@store/products/booking/product/contingent/contingent-booking.actions';
import {
  setContingentReservationDateInBooking,
  setValidatedContingentReservationInBooking
} from '@store/products/booking/product/contingent/contingent-booking.reducer';
import { PackageProductBookingActions } from '@store/products/booking/product/package/package-booking.actions';
import {
  removePackageProductInBookingList,
  setPackageProductInBookingList
} from '@store/products/booking/product/package/package-booking.reducer';
import { ParkingBookingReservationActions } from '@store/products/booking/product/parking/parking-booking.actions';
import { setParkingReservationInBooking } from '@store/products/booking/product/parking/parking-booking.reducer';
import { ProductBookingActions } from '@store/products/booking/product/product-booking.actions';
import {
  removeProductFromBookingList,
  setProductInBookingList
} from '@store/products/booking/product/product-booking.reducer';
import { VoucherProductBookingActions } from '@store/products/booking/product/voucher/voucher-booking.actions';
import {
  removeVoucherProductFromBookingList,
  setVoucherProductInBookingList
} from '@store/products/booking/product/voucher/voucher-booking.reducer';
import { WorkshopBookingActions } from '@store/products/booking/product/workshop/workshop-booking.actions';
import {
  removeWorkshopFromBooking,
  setWorkshopInBooking
} from '@store/products/booking/product/workshop/workshop-booking.reducer';
import { BookingSmartPromotionActions } from '@store/products/booking/smart-promotion/booking-smart-promotion.actions';
import {
  removeBookedProductsPromoCodeDiscount,
  removeBookedProductsPromoCodeDiscountOnChangedProductBookings,
  removeUnredeemedPromoCode,
  setBookedProductsPromoCodeDiscount,
  setUnredeemedPromoCode
} from '@store/products/booking/smart-promotion/booking-smart-promotion.reducer';
import { BookingProductTariffActions } from '@store/products/booking/tariff/booking-tariff.actions';
import {
  removeBookingProductTariffUuid,
  setBookingProductTariffUuid
} from '@store/products/booking/tariff/booking-tariff.reducer';
import { ResetProductsActions } from '@store/products/product.actions';
import { without } from 'lodash';

export { BookingState as State };

const initialState: BookingState = {
  list: [],
  bookedTariffUuids: [],
  countdown: {
    product: null,
    voucher: []
  },
  claimedTicketHash: '',
  isClaimedTicketHashValid: null,
  isAnonymousProductSentToAPI: false,
  promoCodes: []
};

export const setBookedTariffUuids = (
  state: BookingState,
  props: { addBookedTariffUuids: TariffUuids; removeBookedTariffUuids: TariffUuids }
) => ({
  ...state,
  bookedTariffUuids: [
    ...without(state.bookedTariffUuids, ...props.removeBookedTariffUuids),
    ...props.addBookedTariffUuids
  ]
});

export const removeBookedTariffUuids = (state: BookingState, props: { bookedTariffUuids: TariffUuids }) => ({
  ...state,
  bookedTariffUuids: without(state.bookedTariffUuids, ...props.bookedTariffUuids)
});

/**
 * Set claimed ticket hash
 * @description Set claimed ticket hash
 * @param state
 * @returns State
 */
export const setClaimedTicketHash = (state: BookingState, props: { claimedTicketHash: Hash }) => ({
  ...state,
  claimedTicketHash: props.claimedTicketHash
});

/**
 * Set is claimed ticket hash valid
 * @description Set is claimed ticket hash valid
 * @param state
 * @returns State
 */
export const setIsClaimedTicketHashValid = (state: BookingState, props: { isClaimedTicketHashValid: boolean }) => ({
  ...state,
  isClaimedTicketHashValid: props.isClaimedTicketHashValid
});

/**
 * Set is anonymous product send to API
 * @description Set is anonymous product send to API
 * @param state
 * @returns State
 */
export const setIsAnonymousProductSendToAPI = (
  state: BookingState,
  props: { isAnonymousProductSentToAPI: boolean }
) => ({
  ...state,
  isAnonymousProductSentToAPI: props.isAnonymousProductSentToAPI
});

export const bookingReducer = createReducer(
  initialState,
  on(
    ProductBookingActions.setProductInBookingList,
    ProductBookingActions.removeBookedProductsAndSetPreferredProductInBookingList,
    setProductInBookingList
  ),
  on(ProductBookingActions.removeProductFromBookingList, removeProductFromBookingList),
  on(
    VoucherProductBookingActions.setVoucherProductInBookingList,
    VoucherProductBookingActions.setVoucherForAnonymousProductInBookingList,
    setVoucherProductInBookingList
  ),
  on(
    VoucherProductBookingActions.removeVoucherProductFromBookingList,
    VoucherProductBookingActions.removeReleasedVoucherProductFromBookingList,
    removeVoucherProductFromBookingList
  ),
  on(PackageProductBookingActions.setPackageProductInBookingList, setPackageProductInBookingList),
  on(PackageProductBookingActions.removePackageFromBookingList, removePackageProductInBookingList),
  on(BookingProductTariffActions.setBookingProductTariffUuid, setBookingProductTariffUuid),
  on(BookingProductTariffActions.removeBookingProductTariffUuid, removeBookingProductTariffUuid),
  on(WorkshopBookingActions.setWorkshopInBooking, setWorkshopInBooking),
  on(WorkshopBookingActions.removeWorkshopFromBooking, removeWorkshopFromBooking),
  on(ContingentBookingReservationActions.setContingentReservationDateInBooking, setContingentReservationDateInBooking),
  on(
    ContingentBookingReservationActions.setValidatedContingentReservationInBooking,
    setValidatedContingentReservationInBooking
  ),
  on(
    AmanoParkingBookingReservationActions.setAmanoParkingReservationDateInBooking,
    setAmanoParkingReservationDateInBooking
  ),
  on(
    AmanoParkingBookingReservationActions.setValidatedAmanoParkingReservationInBooking,
    setValidatedAmanoParkingReservationDateInBooking
  ),
  on(ParkingBookingReservationActions.setParkingReservationInBooking, setParkingReservationInBooking),
  on(BookingSmartPromotionActions.setBookedProductsPromoCodeDiscount, setBookedProductsPromoCodeDiscount),
  on(BookingSmartPromotionActions.setUnredeemedPromoCode, setUnredeemedPromoCode),
  on(BookingSmartPromotionActions.removeUnredeemedPromoCode, removeUnredeemedPromoCode),
  on(BookingSmartPromotionActions.removeBookedProductsPromoCodeDiscount, removeBookedProductsPromoCodeDiscount),
  on(
    BookingSmartPromotionActions.removeBookedProductsPromoCodeDiscountOnChangedProductBookings,
    removeBookedProductsPromoCodeDiscountOnChangedProductBookings
  ),
  on(BookingCountdownActions.setProductBookingCountdown, setProductBookingCountdown),
  on(BookingCountdownActions.setVoucherProductBookingCountdown, setVoucherProductBookingCountdown),
  on(BookingCountdownActions.removeVoucherProductBookingCountdown, removeVoucherProductBookingCountdown),
  on(BookingCountdownActions.removeProductBookingCountdowns, removeProductBookingCountdowns),
  on(BookingActions.setBookedTariffUuids, setBookedTariffUuids),
  on(BookingActions.removeBookedTariffUuids, removeBookedTariffUuids),
  on(BookingActions.setClaimedTicketHash, setClaimedTicketHash),
  on(BookingActions.setIsClaimedTicketHashValid, setIsClaimedTicketHashValid),
  on(BookingActions.setIsAnonymousProductSentToAPI, setIsAnonymousProductSendToAPI),
  on(BookingActions.resetProductBookings, ResetProductsActions.resetBookingReducer, () => initialState)
);
